import type { UserData } from 'src/types/UserData';

type IcodesFlat = string[];
type IcodesMultiHome = Record<string, string[]>;
export type Icodes = IcodesFlat | IcodesMultiHome;

/**
 * Returns true when the user owns more than one home (multi-unit account).
 */
export const isMultiHome = (userData: Pick<UserData, 'available_units'> | undefined): boolean => {
	return Object.keys(userData?.available_units ?? {}).length > 1;
};

/**
 * Reads installer codes from user_settings.icodes, handling both formats:
 * - Single home: string[] (flat array)
 * - Multi home: { [user_id]: string[] } (object keyed by user_id)
 */
export const getInstallerCodes = (icodes: Icodes | undefined, userId?: string): string[] => {
	if (!icodes) return [];
	if (Array.isArray(icodes)) return icodes;
	if (userId && typeof icodes === 'object') return icodes[userId] ?? [];
	return [];
};

export const addInstallerCode = (
	currentIcodes: Icodes | undefined,
	code: string,
	multiHome: boolean,
	userId?: string,
): Icodes => {
	if (multiHome && userId) {
		const existing = currentIcodes && !Array.isArray(currentIcodes) ? currentIcodes : {};
		const userCodes = getInstallerCodes(currentIcodes, userId);
		if (userCodes.includes(code)) return existing;
		return { ...existing, [userId]: [...userCodes, code] };
	}

	const codes = Array.isArray(currentIcodes) ? currentIcodes : [];
	if (codes.includes(code)) return codes;
	return [...codes, code];
};

/**
 * Removes a code from the icodes structure and returns both the updated
 * structure and the current user's flat list of remaining codes (for emptiness
 * checks). A single pass over the data covers both needs.
 */
export const removeInstallerCode = (
	currentIcodes: Icodes | undefined,
	code: string,
	multiHome: boolean,
	userId?: string,
): { icodes: Icodes; userCodes: string[] } => {
	if (multiHome && userId) {
		const existing = currentIcodes && !Array.isArray(currentIcodes) ? currentIcodes : {};
		const userCodes = getInstallerCodes(currentIcodes, userId).filter((c) => c !== code);
		return { icodes: { ...existing, [userId]: userCodes }, userCodes };
	}

	const userCodes = (Array.isArray(currentIcodes) ? currentIcodes : []).filter((c) => c !== code);
	return { icodes: userCodes, userCodes };
};

const migrateIcodesToMultiHome = (
	currentIcodes: Icodes | undefined,
	userId: string,
): IcodesMultiHome => {
	if (Array.isArray(currentIcodes)) {
		return currentIcodes.length > 0 ? { [userId]: currentIcodes } : {};
	}
	return (currentIcodes as IcodesMultiHome) ?? {};
};

const migrateIcodesToSingleHome = (currentIcodes: Icodes | undefined, userId: string): IcodesFlat => {
	if (currentIcodes && typeof currentIcodes === 'object' && !Array.isArray(currentIcodes)) {
		return currentIcodes[userId] ?? [];
	}
	return Array.isArray(currentIcodes) ? currentIcodes : [];
};

/**
 * Pure migration of icodes when the number of homes changes.
 * Returns the next icodes structure, or `null` when no migration is needed
 * (callers should leave the store unchanged in that case).
 */
export const migrateIcodesOnUnitChange = (
	currentIcodes: Icodes | undefined,
	userId: string,
	previousUnitCount: number,
	currentUnitCount: number,
): Icodes | null => {
	if (!currentIcodes) return null;

	if (previousUnitCount <= 1 && currentUnitCount > 1) {
		return migrateIcodesToMultiHome(currentIcodes, userId);
	}

	if (previousUnitCount > 1 && currentUnitCount <= 1) {
		return migrateIcodesToSingleHome(currentIcodes, userId);
	}

	return null;
};
